import { useEffect, useState, useCallback } from "react";
import bszService from "../services";
import { useAppContext } from "../../../2_ksbHub/context/appContext";

export default function useBszState() {
  const { currentUser, isATeacher, isAStudent, setBszFinderLoading } =
    useAppContext();

  /**
   * =========================================================
   * ✅ STATE
   * =========================================================
   */
  const [rooms, setRooms] = useState([]);
  const [myReservation, setMyReservation] = useState(null);
  const [serverNow, setServerNow] = useState(Date.now());

  const [subjects, setSubjects] = useState([]);
  const [mySubjects, setMySubjects] = useState(null);

  const [classes, setClasses] = useState([]);
  const [myClasses, setMyClasses] = useState(null);
  const [classesOverview, setClassesOverview] = useState([]);

  /**
   * ✅ UI STATE (global dialogs)
   */
  const [showSubjectsDialog, setShowSubjectsDialog] = useState(false);
  const [showSubjectAdminDialog, setShowSubjectAdminDialog] = useState(false);
  const [showClassesDialog, setShowClassesDialog] = useState(false);
  const [showClassesAdminDialog, setShowClassesAdminDialog] = useState(false);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  /**
   * =========================================================
   * ✅ LOADERS
   * =========================================================
   */
  const loadRooms = useCallback(async () => {
    const data = await bszService.getRooms();

    if (Array.isArray(data)) {
      setRooms(data);
      setServerNow(Date.now());
    } else {
      setRooms(data.rooms || []);
      setServerNow(data.serverNow || Date.now());
    }
  }, []);

  const loadClasses = useCallback(async () => {
    const data = await bszService.getClasses();

    const cls = Array.isArray(data?.classes)
      ? data.classes
      : Array.isArray(data)
        ? data
        : [];
    setClasses(cls);
  }, []);

  const loadMyClasses = useCallback(async () => {
    const data = await bszService.getMyClasses();
    setMyClasses(data.classes || []);
  }, []);

  const loadClassesOverview = useCallback(async () => {
    const data = await bszService.getClassesOverview();
    setClassesOverview(data.overview || []);
  }, []);

  const loadSubjects = useCallback(async () => {
    const data = await bszService.getSubjects();
    setSubjects(data.subjects || []);
  }, []);

  const loadMySubjects = useCallback(async () => {
    const data = await bszService.getMySubjects();
    setMySubjects(data.subjects || []);
  }, []);

  /**
   * =========================================================
   * ✅ ✅ ✅ MUTATIONS (NOW CENTRALIZED ✅)
   * =========================================================
   */

  const createRoom = async (roomData) => {
    await bszService.createRoom(roomData);
    await loadRooms();
  };

  const updateRoom = async (roomData) => {
    await bszService.updateRoom(roomData);
    await loadRooms();
  };

  const deleteRoom = async (roomId) => {
    await bszService.deleteRoom(roomId);

    // ✅ optimistic safe update
    setRooms((prev) => prev.filter((r) => r.id !== roomId));
  };

  const generateSlots = async (roomId, studentSlots, teacherSlots) => {
    await bszService.generateSlots({
      roomId,
      studentSlots,
      teacherSlots,
    });

    await loadRooms();
  };

  const moveRoom = ({ roomId, section, row, col }) => {
    let updatedRow = [];

    setRooms((prev) => {
      if (!Array.isArray(prev)) return prev;

      const sameRow = prev
        .filter(
          (r) =>
            r.section === section &&
            (r.layout_row ?? 0) === row &&
            r.id !== roomId,
        )
        .sort((a, b) => (a.layout_col ?? 0) - (b.layout_col ?? 0));

      const dragged = prev.find((r) => r.id === roomId);
      if (!dragged) return prev;

      const insertIndex = Math.max(0, Math.min(col, sameRow.length));

      sameRow.splice(insertIndex, 0, {
        ...dragged,
        section,
        layout_row: row,
      });

      updatedRow = sameRow.map((room, index) => ({
        ...room,
        layout_col: index,
        layout_row: row,
        section,
      }));

      return prev.map((room) => {
        const updated = updatedRow.find((r) => r.id === room.id);
        return updated ? updated : room;
      });
    });

    bszService.updateRoomLayoutBulk(updatedRow).catch((err) => {
      console.error("❌ layout update failed:", err);
    });
  };

  const removeUserFromClass = async (userId, classId) => {
    await bszService.removeUserFromClass(userId, classId);
    await loadClassesOverview();
  };

  const saveMyClasses = async (classIds) => {
    await bszService.saveMyClasses(classIds);
    await loadMyClasses();
  };

  const saveClasses = async (newClasses) => {
    await bszService.saveClasses(newClasses);
    await Promise.all([loadClasses(), loadClassesOverview()]);
  };

  /**
   * =========================================================
   * ✅ INITIAL LOAD
   * =========================================================
   */
  useEffect(() => {
    if (!currentUser?.id) return;

    const loadData = async () => {
      setBszFinderLoading(true);

      try {
        await Promise.all([
          loadRooms(),
          loadClasses(),
          loadSubjects(),

          ...(isATeacher || isAStudent ? [loadMyClasses()] : []),

          ...(isATeacher ? [loadMySubjects(), loadClassesOverview()] : []),
        ]);
      } finally {
        setBszFinderLoading(false);
      }
    };

    loadData();
  }, [
    currentUser?.id,
    isATeacher,
    isAStudent,
    loadRooms,
    loadClasses,
    loadSubjects,
    loadMyClasses,
    loadMySubjects,
    loadClassesOverview,
    setBszFinderLoading,
  ]);

  /**
   * =========================================================
   * ✅ DERIVED STATE
   * =========================================================
   */
  useEffect(() => {
    if (!currentUser || !rooms.length) return;

    for (const room of rooms) {
      const match =
        room.studentSlots?.find((s) => s.reservedBy?.id === currentUser.id) ||
        room.teacherSlots?.find((s) => s.reservedBy?.id === currentUser.id);

      if (match) {
        setMyReservation({
          roomId: room.id,
          name: room.name,
          slotId: match.id,
        });
        return;
      }
    }

    setMyReservation(null);
  }, [rooms, currentUser]);

  /**
   * =========================================================
   * ✅ UI SIDE-EFFECTS
   * =========================================================
   */
  useEffect(() => {
    if (isATeacher && mySubjects?.length === 0) {
      setShowSubjectsDialog(true);
    }
  }, [isATeacher, mySubjects]);

  useEffect(() => {
    if ((isATeacher || isAStudent) && myClasses?.length === 0) {
      setShowClassesDialog(true);
    }
  }, [myClasses, isATeacher, isAStudent]);

  /**
   * =========================================================
   * ✅ reset on logout
   * =========================================================
   */

  useEffect(() => {
    // console.log("BSZ currentUser:", currentUser);
  }, [currentUser]);
  useEffect(() => {
    if (!currentUser) {
      setMySubjects(null);
      setMyClasses(null);
    }
  }, [currentUser]);

  /**
   * =========================================================
   * ✅ EXPORT
   * =========================================================
   */
  return {
    // ✅ state
    rooms,
    myReservation,
    serverNow,
    subjects,
    mySubjects,
    setMySubjects,
    loadSubjects,
    classes,
    myClasses,
    classesOverview,

    // ✅ setters (needed for socket)
    setRooms,
    setServerNow,
    setSnackbar,

    // ✅ UI flags
    snackbar,
    showSubjectsDialog,
    setShowSubjectsDialog,
    showSubjectAdminDialog,
    setShowSubjectAdminDialog,
    showClassesDialog,
    setShowClassesDialog,
    showClassesAdminDialog,
    setShowClassesAdminDialog,

    // ✅ loaders
    loadRooms,
    loadClasses,
    loadMyClasses,
    loadClassesOverview,

    // ✅ MUTATIONS (clean API)
    createRoom,
    updateRoom,
    deleteRoom,
    generateSlots,
    moveRoom,
    removeUserFromClass,
    saveMyClasses,
    saveClasses,
  };
}
